import { call, put, takeLatest } from "redux-saga/effects";
import { ActionWithPayload } from "@shared/interfaces";
import { startLoading, stopLoading } from "@shared/store/actions";

import { SETTINGS_SAVED_MESSAGE } from "../constants";
import {
  SavedSetting,
  SettingSectionView,
  SettingsRequestError,
  SettingUpdate,
  UpdateSettingResponse,
} from "../interfaces";
import { fetchSettings, saveSettings } from "./actions";
import api from "../api";

export function* fetchSettingsSaga() {
  try {
    yield put(startLoading());
    const response: SettingSectionView[] = yield call(api.getSettings);
    yield put(stopLoading());
    yield put(fetchSettings.success(response));
  } catch (error) {
    yield put(fetchSettings.failure(error as SettingsRequestError));
    yield put(stopLoading());
  }
}

// Sequential (a for-loop of individual `call`s), NOT `all(...)` in parallel -- each PUT is its own
// audited row, and running them one at a time means a failure partway through leaves a known,
// already-applied prefix for the refetch below to report, rather than several writes racing each
// other's audit rows. All succeeding dispatches ONE success (one toast, plus every key's newly-saved
// value so the reducer can update `sections` -- see reducer.tsx); any one failing dispatches the
// api's own error and refetches, so the page reflects whatever actually landed.
export function* saveSettingsSaga({ payload }: ActionWithPayload<SettingUpdate[]>) {
  try {
    yield put(startLoading());
    const savedSettings: SavedSetting[] = [];
    for (const update of payload) {
      const response: UpdateSettingResponse = yield call(api.updateSetting, update.key, update.value);
      savedSettings.push({ key: response.appSetting.setting_key, value: Number(response.appSetting.setting_value) });
    }
    yield put(stopLoading());
    yield put(saveSettings.success({ message: SETTINGS_SAVED_MESSAGE, savedSettings }));
  } catch (error) {
    yield put(saveSettings.failure(error as SettingsRequestError));
    yield put(stopLoading());
    yield put(fetchSettings.request());
  }
}

function* settingsSagas() {
  yield takeLatest(fetchSettings.request, fetchSettingsSaga);
  yield takeLatest(saveSettings.request, saveSettingsSaga);
}

export default settingsSagas;
